import React, { useState, useRef, useEffect, useCallback } from "react";
import { Switch, Popover, Modal, Tooltip, notification } from "antd";
import {
  MoreOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  InfoCircleOutlined,
  BellOutlined,
  EyeOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import KpiSparkline from "./KpiSparkline";
import KpiAnomalyAlertInfo from "./KpiAnomalyAlertInfo";
import ScrollingCards from "components/GlobalExecutiveDashboard/Review/ScrollingCards";
import styles from "./kpiView.module.scss";
import { apiRequest } from "util/services";
import {
  resolveKpiTitle,
  resolveKpiPivotSubtitle,
  resolveKpiV3,
  resolveSparklineSeries,
  formatKpiV3Count,
  formatKpiV3Share,
  formatKpiV3Rating,
  formatKpiV3Change,
  formatKpiV3ShareChange,
  formatKpiCompareFooter,
  resolveKpiV3SharePct,
  resolveKpiV3ShareTrendDirection,
  resolveKpiV3TrendDirection,
  resolveKpiMetricDirectionLevel,
  resolveKpiAlertRecipients,
  isKpiAlertEnabled,
  resolveKpiAlertLastCheck,
  formatKpiAlertLastCheckedAt,
  resolveKpiSubtopics,
} from "./kpiViewUtils";
import { trackKpiViewAction } from "util/productAnalytics";

function mapTrendToDirection(trend) {
  if (trend === "up") return "up";
  if (trend === "down") return "down";
  return "neutral";
}

const METRIC_LEVEL_CLASS = {
  success: styles.cardMetricLevelSuccess,
  warning: styles.cardMetricLevelWarning,
  danger: styles.cardMetricLevelDanger,
};

function KpiMetricRow({
  label,
  value,
  change,
  direction,
  performanceLevel,
  valueClassName,
  levelClassName,
  neutral = false,
  actionLabel,
  onClick,
}) {
  const levelClass = levelClassName
    || (neutral
      ? styles.cardMetricLevelNeutral
      : METRIC_LEVEL_CLASS[performanceLevel] || styles.cardMetricLevelNeutral);
  const arrowDirection = direction ?? "flat";
  const deltaClass =
    neutral || arrowDirection === "flat"
      ? styles.cardMetricDeltaNeutral
      : arrowDirection === "up"
      ? styles.cardMetricDeltaUp
      : styles.cardMetricDeltaDown;

  return (
    <div
      className={`${styles.cardMetricRow} ${levelClass} ${
        onClick ? styles.cardMetricRowClickable : ""
      }`}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <span className={styles.cardMetricLabel}>{label}</span>
      <div className={styles.cardMetricValueRow}>
        <span className={`${styles.cardMetricValue} ${valueClassName || ""}`}>
          {value}
        </span>
        {change != null && (
          <span className={`${styles.cardMetricDelta} ${deltaClass}`}>
            {arrowDirection === "up" && (
              <ArrowUpOutlined className={styles.cardMetricDeltaIcon} />
            )}
            {arrowDirection === "down" && (
              <ArrowDownOutlined className={styles.cardMetricDeltaIcon} />
            )}
            {change}
          </span>
        )}
      </div>
      {actionLabel ? (
        <span className={styles.cardMetricSeeComments}>
          <EyeOutlined className={styles.cardMetricSeeCommentsIcon} />
          {actionLabel}
        </span>
      ) : null}
    </div>
  );
}

export default function KpiCard({
  item,
  theme = "dark",
  readOnly = false,
  reviewsPresentationMode = false,
  showMoveAction = false,
  onMenuAction,
  onOpenReviews,
  onReviewsScrolled,
  onToggleAlert,
  onConfigureAlert,
  compareWindow = "30D",
  teamId,
}) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const childFetchRef = useRef(0);
  const [slideIndex, setSlideIndex] = useState(0);
  const [childCache, setChildCache] = useState({});
  const [childLoading, setChildLoading] = useState(false);

  const title = resolveKpiTitle(item);
  const pivotSubtitle = resolveKpiPivotSubtitle(item);
  const subtopics = resolveKpiSubtopics(item);
  const hasSlider = subtopics.length > 0;
  const parentV3 = resolveKpiV3(item);
  const alertEnabled = isKpiAlertEnabled(item);
  const alertRecipients = resolveKpiAlertRecipients(item);
  const alertLastCheck = resolveKpiAlertLastCheck(item);
  const alertLastCheckAt = formatKpiAlertLastCheckedAt(alertLastCheck.at);
  const alertLastCheckTitle = !alertLastCheck.at
    ? t("team_kpi_view.alert_last_check_never", "Not checked yet")
    : (
      <>
        {t("team_kpi_view.alert_last_check_at", {
          time: alertLastCheckAt,
          defaultValue: "Last check: {{time}}",
        })}
        {alertLastCheck.result === "fired" ? (
          <>
            <br />
            {t("team_kpi_view.alert_last_check_fired", "Alert triggered")}
          </>
        ) : null}
        {alertLastCheck.result === "no_anomaly" ? (
          <>
            <br />
            {t(
              "team_kpi_view.alert_last_check_no_anomaly",
              "No anomaly found",
            )}
          </>
        ) : null}
      </>
    );
  const brokenCount = subtopics.filter((row) => row.declined).length;

  useEffect(() => {
    setSlideIndex(0);
    setChildCache({});
    setChildLoading(false);
    childFetchRef.current += 1;
  }, [item?.id, compareWindow]);

  const activeChild = hasSlider && slideIndex > 0 ? subtopics[slideIndex - 1] : null;
  const childPayload = activeChild ? childCache[activeChild.id] : null;
  const v3 = activeChild ? resolveKpiV3(childPayload) : parentV3;
  const sparkSeries = activeChild
    ? resolveSparklineSeries(childPayload || {})
    : resolveSparklineSeries(item);
  const displayTitle = activeChild ? activeChild.label : title;
  const metricsLoading = Boolean(activeChild && childLoading && !v3);
  const reviewItem = activeChild
    ? {
        ...item,
        name: activeChild.label,
        metric: [activeChild.label],
        kpi_v3: childPayload?.kpi_v3 ?? null,
      }
    : item;
  const positivePct = resolveKpiV3SharePct(v3?.positive, v3?.volume);
  const appraisalPct = resolveKpiV3SharePct(
    v3?.appraisal ?? v3?.negative,
    v3?.volume,
  );
  const appraisalMetric = v3?.appraisal ?? v3?.negative;
  const ratingValue = v3?.rating?.current;
  const positiveDirection = mapTrendToDirection(
    resolveKpiV3ShareTrendDirection(v3?.positive, v3?.volume),
  );
  const appraisalDirection = mapTrendToDirection(
    resolveKpiV3ShareTrendDirection(appraisalMetric, v3?.volume),
  );
  const ratingDirection = mapTrendToDirection(
    resolveKpiV3TrendDirection(v3?.rating),
  );

  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeMenu = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("click", closeMenu, true);
    return () => document.removeEventListener("click", closeMenu, true);
  }, [menuOpen]);

  const handleMenu = (action) => {
    setMenuOpen(false);
    if (onMenuAction) onMenuAction(action, item);
  };

  const handleAlertSwitch = (checked) => {
    if (checked) {
      if (onConfigureAlert) onConfigureAlert(item, alertEnabled ? "edit" : "add");
      return;
    }
    Modal.confirm({
      title: t("team_kpi_view.50"),
      content: t("team_kpi_view.51"),
      okText: t("team_kpi_view.64"),
      cancelText: t("button.cancel"),
      centered: true,
      className: theme === "dark" ? styles.anomalyConfirmModalDark : undefined,
      onOk: () => {
        if (onToggleAlert) onToggleAlert(item, false);
      },
    });
  };

  const loadChildMetric = useCallback(
    async (topicId) => {
      if (!teamId || item?.id == null || topicId == null) return;
      const requestId = ++childFetchRef.current;
      setChildLoading(true);
      const result = await apiRequest(
        "executive_summary/v2/kpi_child_metric",
        "POST",
        {
          team: String(teamId),
          kpi_id: item.id,
          topic_id: topicId,
          period: compareWindow === "7D" ? "7D" : "30D",
        },
        true,
      );
      if (requestId !== childFetchRef.current) return;
      if (result?.success) {
        setChildCache((prev) => ({ ...prev, [topicId]: result.success }));
      } else {
        notification.error({
          message:
            result?.error ||
            t("team_kpi_view.unable_kpi_reviews", "Unable to get KPI reviews"),
        });
      }
      setChildLoading(false);
    },
    [teamId, item?.id, compareWindow, t],
  );

  const slideCount = subtopics.length + 1;
  const generalLabel = t("team_kpi_view.subtopic_pager_general", "Genel");
  const goToSlide = (nextIndex) => {
    if (!hasSlider) return;
    const wrapped = ((nextIndex % slideCount) + slideCount) % slideCount;
    setSlideIndex(wrapped);
    if (wrapped === 0) return;
    const child = subtopics[wrapped - 1];
    if (!child || childCache[child.id]) return;
    loadChildMetric(child.id);
  };

  const handleMetricOpen = (metricKey) => (event) => {
    if (event?.stopPropagation) {
      event.stopPropagation();
    }
    if (metricsLoading) return;
    trackKpiViewAction("metric_click", {
      kpiId: item?.id,
      kpiTitle: displayTitle,
      metricKey,
      subtopicId: activeChild?.id || null,
    });
    if (reviewsPresentationMode || !onOpenReviews) return;
    onOpenReviews(reviewItem, { metricKey });
  };

  return (
    <div
      id={item?.id != null ? `kpi-card-${item.id}` : undefined}
      className={`${styles.card} ${alertEnabled ? styles.cardAlertOn : ""} ${
        readOnly ? styles.cardReadOnly : ""
      }`}
    >
      <div className={styles.cardHeader}>
        <div className={styles.cardTitleBlock}>
          <p className={styles.cardTitle}>{displayTitle}</p>
          {activeChild ? (
            <>
              <button
                type="button"
                className={styles.cardBreadcrumb}
                onClick={() => goToSlide(0)}
              >
                {title} ›
              </button>
              {pivotSubtitle ? (
                <span className={styles.cardSubtitle}>{pivotSubtitle}</span>
              ) : null}
            </>
          ) : hasSlider ? (
            <span className={styles.cardSubtitle}>
              {t("team_kpi_view.subtopics_all", "and all subcategories")}
              {pivotSubtitle ? ` · ${pivotSubtitle}` : ""}
            </span>
          ) : (
            pivotSubtitle && (
              <span className={styles.cardSubtitle}>{pivotSubtitle}</span>
            )
          )}
        </div>
        {!readOnly && (
        <div className={styles.cardToolbar}>
          <Popover
            trigger="click"
            placement="bottomRight"
            overlayClassName={
              theme === "dark"
                ? `${styles.anomalyInfoPopover} ${styles.anomalyInfoPopoverDark}`
                : styles.anomalyInfoPopover
            }
            content={<KpiAnomalyAlertInfo theme={theme} />}
          >
            <button
              type="button"
              className={styles.cardToolbarBtn}
              aria-label={t("team_kpi_view.44")}
              onClick={(e) => e.stopPropagation()}
            >
              <InfoCircleOutlined />
            </button>
          </Popover>
          <Tooltip
            title={t(
              "team_kpi_view.92",
              "Get notified when this KPI changes abnormally",
            )}
          >
            <label
              className={styles.cardAlertToggle}
              onClick={(e) => e.stopPropagation()}
            >
              <span
                className={`${styles.cardAlertLabel} ${
                  alertEnabled ? styles.cardAlertLabelOn : ""
                }`}
              >
                {alertEnabled ? t("team_kpi_view.66") : t("team_kpi_view.67")}
              </span>
              <BellOutlined
                className={
                  alertEnabled ? styles.cardAlertBellOn : styles.cardAlertBellOff
                }
              />
              <Switch
                size="small"
                checked={alertEnabled}
                onChange={handleAlertSwitch}
                aria-label={
                  alertEnabled ? t("team_kpi_view.66") : t("team_kpi_view.67")
                }
              />
            </label>
          </Tooltip>
          <button
            type="button"
            className={styles.cardToolbarBtn}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menu"
          >
            <MoreOutlined />
          </button>
        </div>
        )}
      </div>

      {!readOnly && menuOpen && (
        <div className={styles.cardMenu} ref={menuRef}>
          <button
            type="button"
            className={styles.cardMenuItem}
            onClick={() => handleMenu("info")}
          >
            {t("team_kpi_view.25")}
          </button>
          <button
            type="button"
            className={styles.cardMenuItem}
            onClick={() => handleMenu("edit")}
          >
            {t("team_kpi_view.27")}
          </button>
          <button
            type="button"
            className={styles.cardMenuItem}
            onClick={() => handleMenu("rename")}
          >
            {t("button.1")}
          </button>
          {showMoveAction && (
            <button
              type="button"
              className={styles.cardMenuItem}
              onClick={() => handleMenu("move")}
            >
              {t("team_kpi_view.81")}
            </button>
          )}
          <button
            type="button"
            className={styles.cardMenuItem}
            onClick={() => handleMenu("delete")}
          >
            {t("button.2")}
          </button>
        </div>
      )}

      {alertEnabled && (
        <div
          className={styles.cardAlertStrip}
          role="button"
          tabIndex={0}
          onClick={() => {
            if (onConfigureAlert) onConfigureAlert(item, "edit");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && onConfigureAlert) {
              onConfigureAlert(item, "edit");
            }
          }}
        >
          <div className={styles.cardAlertStripHead}>
            <span className={styles.cardAlertStripLabel}>
              {t("team_kpi_view.52")}
            </span>
            <Tooltip title={alertLastCheckTitle} color="var(--ink)">
              <button
                type="button"
                className={styles.cardAlertLastCheckBtn}
                aria-label={t(
                  "team_kpi_view.alert_last_check_aria",
                  "Last alert check",
                )}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <InfoCircleOutlined />
              </button>
            </Tooltip>
          </div>
          <div className={styles.cardAlertEmails}>
            {alertRecipients.length > 0 ? (
              alertRecipients.map((email) => (
                <span key={email} className={styles.cardAlertEmailChip}>
                  {email}
                </span>
              ))
            ) : (
              <span className={styles.cardAlertEmailEmpty}>
                {t("team_kpi_view.53")}
              </span>
            )}
            <span className={styles.cardAlertEmailAdd}>
              + {t("alerts_page.32")}
            </span>
          </div>
        </div>
      )}

      {hasSlider && !activeChild && brokenCount > 0 && (
        <span className={styles.cardSubtopicBadge}>
          {t("team_kpi_view.subtopics_badge", {
            broken: brokenCount,
            total: subtopics.length,
            defaultValue: "{{broken}} / {{total}} alt konu bozuldu",
          })}
        </span>
      )}
      {hasSlider && !activeChild && brokenCount === 0 && (
        <span className={styles.cardSubtopicBadgeMuted}>
          {t("team_kpi_view.subtopics_count", {
            count: subtopics.length,
            defaultValue: "{{count}} alt konu",
          })}
        </span>
      )}
      {activeChild &&
        (activeChild.declined ||
          childPayload?.declined ||
          (!metricsLoading && positiveDirection === "down")) && (
          <span className={styles.cardSubtopicBadge}>
            {t("team_kpi_view.subtopic_broken", "This subtopic deteriorated")}
          </span>
        )}

      {metricsLoading ? (
        <div className={styles.cardMetricsLoading} role="status">
          {t("team_kpi_view.subtopic_loading", "Loading subtopic…")}
        </div>
      ) : v3 ? (
        <div className={styles.cardMetricsGrid}>
          <KpiMetricRow
            label={t("team_kpi_view.metric_volume", "Total reviews")}
            value={formatKpiV3Count(v3.volume?.current)}
            change={formatKpiV3Change(v3.volume)}
            direction={resolveKpiV3TrendDirection(v3.volume)}
            neutral
            valueClassName={styles.cardMetricValuePrimary}
            actionLabel={
              !reviewsPresentationMode && onOpenReviews
                ? t("team_kpi_view.see_comments", "View reviews →")
                : undefined
            }
            onClick={handleMetricOpen("volume")}
          />
          <KpiMetricRow
            label={t("team_kpi_view.metric_positive", "Positive")}
            value={formatKpiV3Share(positivePct)}
            change={formatKpiV3ShareChange(v3.positive, v3.volume)}
            direction={positiveDirection === "neutral" ? "flat" : positiveDirection}
            performanceLevel={resolveKpiMetricDirectionLevel(
              "positive_rate",
              positiveDirection,
            )}
            onClick={handleMetricOpen("positive")}
          />
          <KpiMetricRow
            label={t("welcome.intentAppraisal")}
            value={formatKpiV3Share(appraisalPct)}
            change={formatKpiV3ShareChange(appraisalMetric, v3.volume)}
            direction={appraisalDirection === "neutral" ? "flat" : appraisalDirection}
            performanceLevel={resolveKpiMetricDirectionLevel(
              "praise_rate",
              appraisalDirection,
            )}
            onClick={handleMetricOpen("appraisal")}
          />
          <KpiMetricRow
            label={t("team_kpi_view.metric_rating", "Average rating")}
            value={formatKpiV3Rating(ratingValue)}
            change={formatKpiV3Change(v3.rating, { isRating: true })}
            direction={ratingDirection === "neutral" ? "flat" : ratingDirection}
            performanceLevel={resolveKpiMetricDirectionLevel(
              "average_rating",
              ratingDirection,
            )}
            onClick={handleMetricOpen("rating")}
          />
        </div>
      ) : (
        <div
          className={styles.cardScoreRow}
          onClick={() => {
            if (onOpenReviews) onOpenReviews(reviewItem);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" && onOpenReviews) onOpenReviews(reviewItem);
          }}
        >
          <span className={styles.cardScore}>—</span>
        </div>
      )}

      {reviewsPresentationMode ? (
        <div className={styles.cardReviewsStream}>
          <ScrollingCards
            compact
            reviews={Array.isArray(item.reviews) ? item.reviews : []}
            setAllReviewsScrolled={(done) => {
              if (onReviewsScrolled) onReviewsScrolled(item.id, done);
            }}
          />
          {(!item.reviews || item.reviews.length === 0) && (
            <p className={styles.cardReviewsEmpty}>
              {t("team_kpi_view.reviews_empty", "No recent reviews")}
            </p>
          )}
        </div>
      ) : (
        <KpiSparkline
          values={sparkSeries.values}
          dates={sparkSeries.dates}
          granularity={sparkSeries.granularity}
          tickIntervalDays={
            sparkSeries.granularity === "rolling" ||
            sparkSeries.granularity === "weekly"
              ? 1
              : compareWindow === "7D"
                ? 1
                : 7
          }
          bucketDays={sparkSeries.bucketDays}
          onPointClick={
            onOpenReviews && !metricsLoading
              ? ({ date }) => onOpenReviews(reviewItem, { date })
              : undefined
          }
        />
      )}

      {hasSlider && (
        <div className={styles.cardSubtopicPagerBlock}>
          <div className={styles.cardSubtopicPagerLabel}>
            {t(
              "team_kpi_view.subtopic_pager_label",
              "Alt konular arasında gezin",
            )}
          </div>
          <div className={styles.cardSubtopicPagerRow}>
            <button
              type="button"
              className={styles.cardSubtopicArrow}
              onClick={() => goToSlide(slideIndex - 1)}
              aria-label={t("team_kpi_view.subtopic_prev", "Previous topic")}
            >
              <LeftOutlined />
            </button>
            <div className={styles.cardSubtopicPagerCenter} aria-live="polite">
              {slideIndex === 0 ? (
                <>
                  <span className={styles.cardSubtopicPagerActive}>
                    {generalLabel}
                  </span>
                  <span className={styles.cardSubtopicPagerMeta}>
                    {" · "}
                    {slideIndex + 1} / {slideCount}
                  </span>
                </>
              ) : (
                <>
                  <span className={styles.cardSubtopicPagerParent}>
                    {generalLabel}
                  </span>
                  <span className={styles.cardSubtopicPagerActive}>
                    {" · "}
                    {activeChild?.label ||
                      t("team_kpi_view.subtopic_n", {
                        index: slideIndex,
                        defaultValue: "Subtopic {{index}}",
                      })}
                  </span>
                  <span className={styles.cardSubtopicPagerMeta}>
                    {" · "}
                    {slideIndex + 1} / {slideCount}
                  </span>
                </>
              )}
            </div>
            <button
              type="button"
              className={styles.cardSubtopicArrow}
              onClick={() => goToSlide(slideIndex + 1)}
              aria-label={t("team_kpi_view.subtopic_next", "Next topic")}
            >
              <RightOutlined />
            </button>
          </div>
          <div className={styles.cardSubtopicDots} role="tablist">
            {Array.from({ length: slideCount }, (_, index) => (
              <button
                key={`dot-${item?.id}-${index}`}
                type="button"
                role="tab"
                aria-selected={index === slideIndex}
                className={
                  index === slideIndex
                    ? styles.cardSubtopicDotActive
                    : styles.cardSubtopicDot
                }
                onClick={() => goToSlide(index)}
                aria-label={
                  index === 0
                    ? generalLabel
                    : subtopics[index - 1]?.label ||
                      t("team_kpi_view.subtopic_n", {
                        index,
                        defaultValue: "Subtopic {{index}}",
                      })
                }
              />
            ))}
          </div>
        </div>
      )}

      <div className={styles.cardFooter}>
        {formatKpiCompareFooter(compareWindow, t)}
      </div>
    </div>
  );
}
